import { useEffect, useState } from "react";
import { Sparkles, Copy, Mail, Plus, Trash2, Send } from "lucide-react";
import { toast } from "sonner";
import { PageHead, Card } from "@/pages/app/AppShell";
import { useAuth } from "@/context/AuthContext";
import { api, API_BASE, errMsg } from "@/lib/api";

const fill = (s, v) => s.replace(/\{\{(\w+)\}\}/g, (m, k) => v[k] || m);

export default function Templates() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [sel, setSel] = useState(null);
  const [v, setV] = useState({ first_name: "", company_id: "", their_company: "", event: "", notes: "", tone: "Warm & professional" });
  const [draft, setDraft] = useState({ subject: "", body: "" });
  const [ai, setAi] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/templates").then((r) => { const l = Array.isArray(r.data) ? r.data : []; setList(l); if (!sel && l[0]) pick(l[0]); }).catch(() => {});
  useEffect(() => { load(); api.get("/companies").then((r) => setCompanies(Array.isArray(r.data) ? r.data : [])).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  function pick(t) { setSel(t); setDraft({ subject: t.subject, body: t.body }); setAi(""); }
  const vars = { ...v, my_company: user?.company?.name || "", my_name: user?.name || "" };
  const filled = { subject: fill(draft.subject, vars), body: fill(draft.body, vars) };
  const output = ai || `Subject: ${filled.subject}\n\n${filled.body}`;

  const personalize = async () => {
    setBusy(true); setAi("");
    try {
      const res = await fetch(`${API_BASE}/templates/personalize`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ subject: draft.subject, body: draft.body, recipient_name: v.first_name, company_id: v.company_id, company_name: v.their_company, notes: [v.event && `Event: ${v.event}`, v.notes].filter(Boolean).join(". "), tone: v.tone }) });
      if (!res.ok || !res.body) throw new Error("AI personalization failed");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        parts.forEach((p) => {
          if (!p.startsWith("data: ")) return;
          const d = JSON.parse(p.slice(6));
          if (d.delta) setAi((x) => x + d.delta);
          if (d.error) toast.error(d.error);
        });
      }
    } catch (e) { toast.error(e.message || "AI personalization failed"); } finally { setBusy(false); }
  };
  const saveAsTemplate = async () => {
    const name = window.prompt("Template name", sel?.name ? `${sel.name} (custom)` : "My template");
    if (!name) return;
    try { await api.post("/templates", { name, category: "Custom", subject: draft.subject, body: draft.body }); toast.success("Template saved"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const subj = output.match(/^Subject:\s*(.*)$/m)?.[1] || filled.subject;
  const body = output.replace(/^Subject:.*\n+/m, "");
  const sendInApp = async () => {
    if (!v.company_id) return toast.error("Pick a company on Balqis Book to message them");
    try {
      const { data } = await api.post("/messages/threads", { company_id: v.company_id });
      await api.post(`/messages/threads/${data.id}`, { kind: "text", body: `${subj}\n\n${body}`, meta: {} });
      toast.success("Sent as a message");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="templates-page">
      <PageHead kicker="Outreach templates" title="Email templates" />
      <div className="grid gap-6 lg:grid-cols-12">
        <Card className="lg:col-span-3 p-3 h-fit" data-testid="template-list">
          {list.map((t) => (
            <div key={t.id} className={`group flex items-center justify-between rounded-xl px-3 py-2.5 cursor-pointer ${sel?.id === t.id ? "bg-[#E0218A]/12 border border-[#E0218A]/30" : "hover:bg-[#2A1240]/5 border border-transparent"}`} onClick={() => pick(t)} data-testid={`template-${t.id}`}>
              <div><p className="text-sm text-[#2A1240]">{t.name}</p><p className="text-[11px] text-slate-400">{t.built_in ? t.category : "Custom"}</p></div>
              {!t.built_in && <button onClick={(e) => { e.stopPropagation(); api.delete(`/templates/${t.id}`).then(load); }} className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-300" aria-label="Delete"><Trash2 className="h-3.5 w-3.5" /></button>}
            </div>
          ))}
        </Card>
        <Card className="lg:col-span-4 p-6 space-y-3" data-testid="template-editor">
          <p className="kicker">Recipient</p>
          <input className="field" placeholder="First name (e.g. Sarah)" value={v.first_name} onChange={(e) => setV({ ...v, first_name: e.target.value })} data-testid="tpl-first-name" />
          <select className="field" value={v.company_id} onChange={(e) => { const c = companies.find((x) => x.id === e.target.value); setV({ ...v, company_id: e.target.value, their_company: c?.name || "" }); }} data-testid="tpl-company-select"><option value="">Company on Balqis Book…</option>{companies.filter((c) => c.id !== user?.company_id).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
          {!v.company_id && <input className="field" placeholder="…or type their company" value={v.their_company} onChange={(e) => setV({ ...v, their_company: e.target.value })} data-testid="tpl-company-input" />}
          <input className="field" placeholder="Event / campaign (optional)" value={v.event} onChange={(e) => setV({ ...v, event: e.target.value })} data-testid="tpl-event" />
          <textarea rows={2} className="field" placeholder="Anything the AI should mention" value={v.notes} onChange={(e) => setV({ ...v, notes: e.target.value })} data-testid="tpl-notes" />
          <select className="field" value={v.tone} onChange={(e) => setV({ ...v, tone: e.target.value })} data-testid="tpl-tone">{["Warm & professional", "Concise & direct", "Luxury & refined", "Energetic & bold"].map((t) => <option key={t}>{t}</option>)}</select>
          <p className="kicker pt-2">Template</p>
          <input className="field" value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} data-testid="tpl-subject" />
          <textarea rows={7} className="field font-mono text-xs" value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} data-testid="tpl-body" />
          <button className="text-xs text-[#8E2BC4]" onClick={saveAsTemplate} data-testid="tpl-save-template"><Plus className="inline h-3 w-3" /> Save as my template</button>
        </Card>
        <Card className="lg:col-span-5 p-6" data-testid="template-preview">
          <div className="flex items-center justify-between"><p className="kicker">{ai ? "AI-personalized" : "Preview"}</p>
            <button className="btn-gold !py-2 !px-4 !text-xs" disabled={busy} onClick={personalize} data-testid="tpl-personalize-button"><Sparkles className="h-3.5 w-3.5" />{busy ? "Writing…" : ai ? "Rewrite" : "Personalize with AI"}</button></div>
          <pre className="mt-4 whitespace-pre-wrap font-body text-sm leading-relaxed text-slate-100 min-h-[260px]" data-testid="tpl-output">{output}{busy && <span className="inline-block w-2 h-4 bg-[#E0218A] animate-pulse align-middle ml-0.5" />}</pre>
          <div className="mt-5 flex flex-wrap gap-2">
            <button className="btn-ghost !py-2 !px-3 !text-xs" onClick={() => { navigator.clipboard?.writeText(output); toast.success("Copied"); }} data-testid="tpl-copy"><Copy className="h-3.5 w-3.5" />Copy</button>
            <a className="btn-ghost !py-2 !px-3 !text-xs" href={`mailto:?subject=${encodeURIComponent(subj)}&body=${encodeURIComponent(body)}`} data-testid="tpl-mailto"><Mail className="h-3.5 w-3.5" />Open in mail app</a>
            <button className="btn-ghost !py-2 !px-3 !text-xs" onClick={sendInApp} data-testid="tpl-send-message"><Send className="h-3.5 w-3.5" />Send as Balqis message</button>
          </div>
        </Card>
      </div>
    </div>
  );
}
